// -----------------------------------------------------------------------------
// certificatePrintService.tsx
//
// Builds the HCI "Certificate of Completion" as a standalone HTML document
// and prints it via a popup window. Shares the same header/legal block,
// corner flourish, seals, and QR verification treatment as
// marksheetPrintService.tsx — same logo files, same LOGO_BASE approach.
// -----------------------------------------------------------------------------

import React from 'react';
import hciLogo from './hci-logo.png';
import isoSeal from './iso.png';
import aaiobarSeal from './aaiobar.png';
import smcsSeal from './iso-smcs.png';
import msmeSeal from './msme.png';
import cornerShape from './shape.png';

const LOGOS = {
  hci: hciLogo,
  iso: isoSeal,
  aaiobar: aaiobarSeal,
  smcs: smcsSeal,
  msme: msmeSeal,
  corner: cornerShape,
};

// Static text shown at the bottom of the printed document. The QR/barcode
// itself still encodes the dynamic per-student verificationUrl (built from
// document_verifier_key) — this footer string never changes.
const STATIC_VERIFY_URL_TEXT = 'https://hciindia.in/verify/';

export interface CertificateData {
  studentName: string;
  /** e.g. "S/O", "D/O", "C/O" */
  guardianRelation: string;
  guardianName: string;
  courseName: string;
  batchStartLabel: string;
  batchEndLabel: string;
  studyCentreName: string;
  studyCentreAddress: string;
  grade: string;
  percentage: number;
  enrollmentNo: string;
  certificateNo: string;
  issueDate: string;
  /** Encoded into the QR/barcode. Defaults to https://www.hciindia.in/student-verification?cert=<certificateNo> if omitted. */
  verificationUrl?: string;
}

function escapeHtml(str: string) {
  const map: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  return str.replace(/[&<>"']/g, (c) => map[c]);
}

export function generateCertificateHTML(data: CertificateData, qrCodeDataUrl: string): string {
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>Certificate of Completion - ${escapeHtml(data.studentName)}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    background: #eef1f6;
    font-family: 'Arial', 'Helvetica', sans-serif;
    display: flex;
    justify-content: center;
    padding: 30px;
  }
  .certificate {
    width: 900px;
    max-width: 100%;
    min-height: 826px;
    background: #ffffff;
    position: relative;
    padding: 11px;
    border: 3px solid #1a2a6b;
  }
  .inner-border {
    border: 1px solid #1a2a6b;
    padding: 42px 49px 35px;
    min-height: calc(826px - 22px - 2px);
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  /* Same shape.png orientation logic as the marksheet: native art is
     top-right (knot at top, vine down the right edge); every other corner
     mirrors off that. */
  .corner { position: absolute; width: 56px; height: 75px; pointer-events: none; }
  .corner img { width: 100%; height: 100%; object-fit: contain; display: block; }
  .corner.tr { top: 4px; right: 4px; }
  .corner.tl { top: 4px; left: 4px; transform: scaleX(-1); }
  .corner.br { bottom: 4px; right: 4px; transform: scaleY(-1); }
  .corner.bl { bottom: 4px; left: 4px; transform: scale(-1,-1); }

  .header-flex { display: flex; align-items: center; justify-content: center; margin-bottom: 11px; }
  .header-flex img.hci-logo { height: 70px; }

  .legal-block {
    text-align: center; color: #b02a2a; font-weight: 700; font-size: 12.5px;
    line-height: 1.5; margin-bottom: 10px;
  }
  .address-block { text-align: center; color: #1a2a6b; font-size: 12.5px; font-weight: 700; line-height: 1.4; margin-bottom: 24px; }

  .cert-title {
    text-align: center; font-size: 42px; font-weight: 700; color: #111;
    margin-bottom: 28px; font-family: 'Georgia', 'Times New Roman', serif;
    letter-spacing: 0.5px;
  }

  .awarded-to { text-align: center; color: #b02a2a; font-weight: 700; font-size: 14.5px; margin-bottom: 13px; }
  .student-name {
    text-align: center; font-size: 30px; font-weight: 400; color: #111;
    text-decoration: underline; text-underline-offset: 8px; margin-bottom: 18px;
  }
  .on-completion { text-align: center; color: #b02a2a; font-weight: 700; font-style: italic; font-size: 14px; margin-bottom: 11px; }
  .course-name { text-align: center; font-size: 23px; color: #111; margin-bottom: 22px; }

  .period-line { text-align: center; color: #b02a2a; font-size: 14.5px; margin-bottom: 10px; }
  .period-line u { font-family: 'Courier New', monospace; }
  .centre-label { text-align: center; font-size: 13.5px; color: #111; margin-bottom: 7px; }
  .centre-detail { text-align: center; font-size: 15px; color: #111; line-height: 1.4; margin-bottom: 39px; }
  .centre-detail u { }

  .meta-row { display: flex; justify-content: space-between; margin-bottom: 32px; font-size: 14px; color: #111; line-height: 1.4; }
  .meta-row .right { text-align: right; }
  .meta-row .meta-label { font-weight: 700; }

  .bottom-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 28px; }
  .seals-block { display: flex; gap: 14px; align-items: center; flex-shrink: 0; }
  .seals-block img { height: 72px; width: auto; object-fit: contain; }

  .sig-block { text-align: center; }
  .sig-line { border-top: 1.5px solid #333; width: 170px; margin: 32px auto 4px; }
  .sig-label { font-size: 13.5px; font-weight: 700; color: #222; }

  .qr-row { display: flex; justify-content: center; margin-bottom: 10px; }
  .qr-row img { width: 92px; height: 92px; display: block; }

  .footer-note { text-align: center; font-size: 12px; font-style: italic; color: #333; }

  @media print {
    body { background: #fff; padding: 0; }
    .certificate { box-shadow: none; }
  }
</style>
</head>
<body>
  <div class="certificate">
    <div class="inner-border">
      <div class="corner tl"><img src="${LOGOS.corner}" alt="" /></div>
      <div class="corner tr"><img src="${LOGOS.corner}" alt="" /></div>
      <div class="corner bl"><img src="${LOGOS.corner}" alt="" /></div>
      <div class="corner br"><img src="${LOGOS.corner}" alt="" /></div>

      <div class="header-flex">
        <img class="hci-logo" src="${LOGOS.hci}" alt="Hindustan Computer Institute" />
      </div>

      <div class="legal-block">
        Registered Trademark of Parshuram Skills Academy (OPC) Pvt. Ltd.<br>
        Incorporated Under the Companies Act, 2013 (18 of 2013),<br>
        Ministry of Corporate Affairs, Government of India.<br>
        CIN: U85499JK2023OPC014559<br>
        ISO 9001:2015 Certified
      </div>

      <div class="address-block">
        Billawar Phinter Road Near Sacred Heart Church Dher, Billawar, Kathua, J&amp;K<br>
        www.hciindia.in
      </div>

      <div class="cert-title">Certificate of Completion</div>

      <div class="awarded-to">This certificate is proudly awarded to</div>
      <div class="student-name">${escapeHtml(data.studentName.toUpperCase())} ${escapeHtml(data.guardianRelation)} ${escapeHtml(data.guardianName)}</div>

      <div class="on-completion">on completion of</div>
      <div class="course-name">${escapeHtml(data.courseName)}</div>

      <div class="period-line">in the period of <u>${escapeHtml(data.batchStartLabel)}</u> to ${escapeHtml(data.batchEndLabel)}</div>
      <div class="centre-label">from our Study Centre</div>
      <div class="centre-detail">
        <u>${escapeHtml(data.studyCentreName)}</u>, ${escapeHtml(data.studyCentreAddress)}<br>
        with Grade <u>${escapeHtml(data.grade)}</u> and <u>${data.percentage.toFixed(2)}%</u> Percentage
      </div>

      <div class="meta-row">
        <div><span class="meta-label">Enrollment Number :</span> ${escapeHtml(data.enrollmentNo)}</div>
        <div class="right">
          <div><span class="meta-label">Certificate Number :</span> ${escapeHtml(data.certificateNo)}</div>
          <div><span class="meta-label">Issue Date :</span> ${escapeHtml(data.issueDate)}</div>
        </div>
      </div>

      <div class="bottom-row">
        <div class="seals-block">
          <img src="${LOGOS.iso}" alt="ISO 9001:2015" />
          <img src="${LOGOS.aaiobar}" alt="AIAO-BAR Accredited" />
          <img src="${LOGOS.smcs}" alt="SMCS Certification" />
          <img src="${LOGOS.msme}" alt="MSME Registered" />
        </div>
        <div class="sig-block">
          <div class="sig-line"></div>
          <div class="sig-label">Director</div>
        </div>
      </div>

      <div class="qr-row">
        <img src="${qrCodeDataUrl}" alt="Scan to verify" />
      </div>
      <div class="footer-note">Note: To verify Scan this QR Code or Visit ${escapeHtml(STATIC_VERIFY_URL_TEXT)}</div>
    </div>
  </div>
</body>
</html>`;
}

/**
 * Opens a popup window with the rendered certificate and triggers the
 * browser print dialog once images have loaded. Requires the `qrcode`
 * package (already added for the marksheet service):
 *   npm install qrcode
 *   npm install --save-dev @types/qrcode
 */
export async function printCertificate(data: CertificateData): Promise<void> {
  // Open the window synchronously (before the first await) so browsers don't
  // treat it as an unrequested popup and block it.
  const printWindow = window.open('', '_blank', 'width=1000,height=1300');
  if (!printWindow) {
    // eslint-disable-next-line no-alert
    alert('Please allow pop-ups for this site to print the certificate.');
    return;
  }
  printWindow.document.write('<p style="font-family:sans-serif;padding:20px;">Preparing certificate…</p>');

  // What the QR/barcode itself encodes — the dynamic per-student
  // verification URL built from document_verifier_key.
  const qrVerificationUrl =
    data.verificationUrl ?? `https://www.hciindia.in/student-verification?cert=${encodeURIComponent(data.certificateNo)}`;

  let qrCodeDataUrl: string;
  try {
    const QRCode = await import('qrcode');
    qrCodeDataUrl = await QRCode.toDataURL(qrVerificationUrl, {
      margin: 0,
      width: 200,
      color: { dark: '#1a1a1a', light: '#ffffff' },
    });
  } catch (err) {
    console.error('Failed to generate verification QR code', err);
    qrCodeDataUrl = ''; // falls back to a broken-image icon rather than blocking print entirely
  }

  const html = generateCertificateHTML(data, qrCodeDataUrl);
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.onload = () => {
    printWindow.focus();
    printWindow.print();
  };
}

/**
 * Placeholder data so the print flow can be exercised end-to-end before
 * real student/enrollment data is wired up.
 */
export function getDummyCertificateData(): CertificateData {
  return {
    studentName: 'John Doe',
    guardianRelation: 'S/O',
    guardianName: 'Robert Doe',
    courseName: 'Sample Course (Print Preview)',
    batchStartLabel: 'April 2026',
    batchEndLabel: 'March 2027',
    studyCentreName: 'Hindustan Computer Institute',
    studyCentreAddress: 'Billawar Phinter Road Near Sacred Heart Church Dher, Billawar, Kathua, J&K',
    grade: 'A+',
    percentage: 85.14,
    enrollmentNo: 'DUMMY0001',
    certificateNo: 'CERT-DUMMY',
    issueDate: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
  };
}